import {
  SavedSearchesContext,
  SavedSearchesHooks,
  SearchInput,
  SucceededResultGraphType,
} from '@access-erp/common';
import { JSX, PropsWithChildren, useMemo } from 'react';

import { useGetSavedSearchesLazyQuery } from '../hooks/SavedSearches/useGetSavedSearchesLazyQuery';
import { useGetSavedSearchLazyQuery } from '../hooks/SavedSearches/useGetSavedSearchLazyQuery';
import { useSaveSearchMutation } from '../hooks/SavedSearches/useSaveSearchMutation';
import { useUpdateSavedSearchMutation } from '../hooks/SavedSearches/useUpdateSavedSearchMutation';
import { useDeleteSavedSearchMutation } from '../hooks/SavedSearches/useDeleteSavedSearchMutation';

import { Forms } from '@/graphql/contracts/_generated_/contracts';
import { convertForGet, mapSavedSearchResult } from '../utils/savedSearch';

export const SavedSearchesProvider = <TFilter,>({ children }: PropsWithChildren): JSX.Element => {
  const [getSearchesList, { data: listResult, loading: listLoading, refetch }] =
    useGetSavedSearchesLazyQuery();
  const [getSavedSearch, { data: byKeyResult }] = useGetSavedSearchLazyQuery();
  const [insertSearch, { data: insertData, loading: insertLoading, error: insertError }] =
    useSaveSearchMutation();
  const [updateSearch, { data: updateData, loading: updateLoading, error: updateError }] =
    useUpdateSavedSearchMutation();
  const [deleteSearch, { data: deleteData, loading: deleteLoading, error: deleteError }] =
    useDeleteSavedSearchMutation();

  const listData = useMemo(() => mapSavedSearchResult(listResult), [listResult]);
  const byKeyData = convertForGet<TFilter>(byKeyResult?.config?.savedSearches?.[0]);

  const getSavedSearches = async (form: Forms) => {
    await getSearchesList({ variables: { form } });
  };
  const getSavedSearchByRowKey = async (form: Forms, rowKey: string) => {
    await getSavedSearch({ variables: { form, rowKey } });
  };
  const handleRefetch = async () => {
    await refetch();
  };

  const handleInsertSearch = async (searchInput: SearchInput<Forms>) => {
    const insertResult = await insertSearch({ variables: { search: searchInput } });
    return insertResult?.data?.config?.savedSearch?.saveSearch;
  };

  const handleUpdateSearch = async (searchInput: SearchInput<Forms>) => {
    const updateResult = await updateSearch({ variables: { search: searchInput } });
    return updateResult?.data?.config?.savedSearch?.updateSearch;
  };

  const handleDeleteSearch = async (
    form: Forms,
    rowKey: string,
  ): Promise<SucceededResultGraphType> => {
    const deleteResult = await deleteSearch({ variables: { form, rowKey } });
    return deleteResult?.data?.config?.savedSearch?.deleteSearch as SucceededResultGraphType;
  };

  const value: SavedSearchesHooks<TFilter, Forms> = useMemo(
    () => ({
      getSavedSearches,
      refetch: handleRefetch,
      savedSearchesList: listData,
      listLoading,
      getSavedSearchByRowKey,
      byKeyData,
      insertSearch: handleInsertSearch,
      insertLoading,
      insertError,
      insertData: insertData?.config?.savedSearch?.saveSearch,
      updateSearch: handleUpdateSearch,
      updateLoading,
      updateError,
      updateData: updateData?.config?.savedSearch?.updateSearch,
      deleteSearch: handleDeleteSearch,
      deleteLoading,
      deleteError,
      deleteData: deleteData?.config?.savedSearch?.deleteSearch,
    } as SavedSearchesHooks<TFilter, Forms>),
    [getSavedSearches],
  );

  return <SavedSearchesContext.Provider value={value as any}>{children}</SavedSearchesContext.Provider>;
};
